/**
 * AGC: Suggestion Form Component
 * AGC: Allows users to submit feedback and suggestions
 */

import React, { useState, useEffect } from 'react'
import { X, Send, MessageSquare, Clock, ChevronRight } from 'lucide-react'
import { buildBackendUrl } from '../utils/backendUrl'

interface Comment {
  id: string
  text: string
  createdAt: number
}

interface Suggestion {
  id: string
  title: string
  category: string
  text: string
  createdAt: number
  updatedAt: number
  commentCount: number
  comments?: Comment[]
}

interface SuggestionFormProps {
  onClose: () => void
  onSubmitSuccess?: () => void
  onViewAll?: () => void
  onSuggestionClick?: (suggestion: Suggestion) => void
}

const SUGGESTION_CATEGORIES = [
  'Token',
  'Game Mechanics',
  'Blockchain',
  'UI/UX',
  'Buffs & NFTs',
  'Battle System',
  'Other'
]

export const SuggestionForm: React.FC<SuggestionFormProps> = ({ onClose, onSubmitSuccess, onViewAll, onSuggestionClick }) => {
  const [title, setTitle] = useState('')
  const [category, setCategory] = useState('')
  const [text, setText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  
  // AGC: Recent suggestions state
  const [recentSuggestions, setRecentSuggestions] = useState<Suggestion[]>([])
  const [isLoadingRecent, setIsLoadingRecent] = useState(false)
  const RECENT_LIMIT = 5 // AGC: Show last 5 suggestions

  // AGC: Fetch recent suggestions
  useEffect(() => {
    fetchRecentSuggestions()
  }, [])

  const fetchRecentSuggestions = async () => {
    try {
      setIsLoadingRecent(true)
      const response = await fetch(buildBackendUrl('api/suggestions'))

      if (!response.ok) {
        throw new Error('Failed to fetch suggestions')
      }

      const data = await response.json()
      // AGC: Get the most recent suggestions (limit to 5)
      setRecentSuggestions((data.suggestions || []).slice(0, RECENT_LIMIT))
    } catch (err: any) {
      console.error('Failed to load recent suggestions:', err)
    } finally {
      setIsLoadingRecent(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!title.trim() || !category || !text.trim()) {
      setError('Please fill in all fields')
      return
    }

    if (title.length > 200) {
      setError('Title must be 200 characters or less')
      return
    }

    if (text.length > 5000) {
      setError('Text must be 5000 characters or less')
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch(buildBackendUrl('api/suggestions'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title: title.trim(), category, text: text.trim() })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to submit suggestion')
      }

      setSuccess(true)
      setTitle('')
      setCategory('')
      setText('')

      // AGC: Refresh recent suggestions after successful submission
      await fetchRecentSuggestions()

      // AGC: Call success callback after a short delay
      setTimeout(() => {
        if (onSubmitSuccess) {
          onSubmitSuccess()
        }
        onClose()
      }, 1500)
    } catch (err: any) {
      setError(err.message || 'Failed to submit suggestion. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatDate = (timestamp: number) => {
    try {
      const date = new Date(timestamp)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins}m ago`
      if (diffHours < 24) return `${diffHours}h ago`
      if (diffDays < 7) return `${diffDays}d ago`
      return date.toLocaleDateString()
    } catch {
      return 'Unknown date'
    }
  }

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Token': 'bg-yellow-500/20 text-yellow-400',
      'Game Mechanics': 'bg-blue-500/20 text-blue-400',
      'Blockchain': 'bg-purple-500/20 text-purple-400',
      'UI/UX': 'bg-pink-500/20 text-pink-400',
      'Buffs & NFTs': 'bg-green-500/20 text-green-400',
      'Battle System': 'bg-red-500/20 text-red-400',
      'Other': 'bg-gray-500/20 text-gray-400'
    }
    return colors[category] || colors['Other']
  }

  return (
    <div className="w-full max-w-sm mx-auto space-y-3">
      {/* AGC: Hero Header - Following design_tips.md pattern */}
      <section className="pb-2 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">SUGGESTIONS</p>
            <h1 className="text-2xl font-bold leading-tight text-white">💡 Leave Feedback</h1>
          </div>
          {onViewAll && (
            <button
              onClick={onViewAll}
              className="text-[11px] text-blue-400 hover:text-blue-300 transition-colors"
            >
              View All
            </button>
          )}
        </div>
      </section>

      {/* AGC: Compact form layout following design_tips.md */}
      <section className="rounded-xl bg-slate-900/80 px-3 py-2 space-y-3">

      {error && (
        <div className="p-2 bg-red-900/50 border border-red-500 rounded-lg text-red-200 text-[11px]">
          {error}
        </div>
      )}

      {success && (
        <div className="p-2 bg-green-900/50 border border-green-500 rounded-lg text-green-200 text-[11px]">
          ✅ Suggestion submitted successfully!
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label htmlFor="title" className="block text-[11px] font-semibold text-slate-300 mb-1.5">
            Title *
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Brief title..."
            required
            disabled={isSubmitting}
          />
          <div className="text-[10px] text-slate-400 mt-1">{title.length}/200</div>
        </div>

        <div>
          <label htmlFor="category" className="block text-[11px] font-semibold text-slate-300 mb-1.5">
            Category *
          </label>
          <select
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
            disabled={isSubmitting}
          >
            <option value="">Select category...</option>
            {SUGGESTION_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="text" className="block text-[11px] font-semibold text-slate-300 mb-1.5">
            Description *
          </label>
          <textarea
            id="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={5000}
            rows={6}
            className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            placeholder="Describe your suggestion..."
            required
            disabled={isSubmitting}
          />
          <div className="text-[10px] text-slate-400 mt-1">{text.length}/5000</div>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !title.trim() || !category || !text.trim()}
            className="flex-1 px-3 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-3 h-3" />
                Submit
              </>
            )}
          </button>
        </div>
      </form>
      </section>

      {/* AGC: Recent Suggestions - Compact section */}
      {recentSuggestions.length > 0 && (
        <section className="rounded-xl bg-slate-900/80 px-3 py-2 space-y-2">
          <h3 className="text-[13px] font-semibold text-white">Recent Suggestions</h3>
          {isLoadingRecent ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {recentSuggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  onClick={() => {
                    if (onSuggestionClick) {
                      onSuggestionClick(suggestion)
                    }
                  }}
                  className="w-full text-left p-2 bg-slate-800/50 hover:bg-slate-800 rounded-lg transition-colors border border-slate-700/50"
                >
                  <div className="flex items-start gap-2 mb-1">
                    <h4 className="text-[11px] font-semibold text-white flex-1 truncate">{suggestion.title}</h4>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap flex-shrink-0 ${getCategoryColor(suggestion.category)}`}>
                      {suggestion.category}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-300 line-clamp-1 mb-1">{suggestion.text}</p>
                  <div className="flex items-center gap-2 text-[10px] text-slate-400">
                    <div className="flex items-center gap-0.5">
                      <MessageSquare className="w-3 h-3" />
                      <span>{suggestion.commentCount || 0}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Clock className="w-3 h-3" />
                      <span>{formatDate(suggestion.createdAt)}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  )
}

